/**
 * Hermes Chat's reads and writes (design-intelligence §2.3).
 *
 * ## Streaming: what the procedure actually offers
 *
 * Nothing. `contract.test.ts` states it as a deliberate omission — "no streaming
 * endpoint (a turn is bounded at eight tool calls and answers in one response)"
 * — and `CRM_EVENT_KINDS` has no chat frame either, so there is no SSE nudge to
 * poll on. Polling `chat.history` mid-turn would also return nothing useful: the
 * whole turn is written inside ONE transaction by `persistTurn`, so the tool
 * traffic is invisible until the reply is too.
 *
 * So the surface is request/response, and the UI says so honestly: the user's
 * message is echoed optimistically, a working indicator names the bound ("up to
 * eight lookups"), and the whole turn — tool calls included — arrives at once.
 * Faking a token stream over a single response would be a lie about latency, and
 * a poll loop against a transaction that has not committed is a lie about
 * progress.
 *
 * ## What `chat.send` can return that is NOT an error
 *
 * A DROPPED turn: `reply: null` with a `droppedReason` and a human-readable
 * `message`. Guard failures, an exhausted budget, an unconfigured model and an
 * over-large input all land here as 200s, because every one of them is product
 * behaviour a person has to see. `useMutation`'s `onError` would never fire for
 * them, which is why the transcript renders `droppedReason` as a first-class row
 * rather than treating a null reply as an empty message.
 *
 * ## The confirmation gate
 *
 * `chat.confirm` takes an `actionId` and nothing else — the payload that
 * executes is the server's stored copy. That is the reason this file exposes NO
 * way to pass a payload to `confirm`: a hook with that parameter would be an
 * invitation to build a UI that edits a proposal before approving it, which is
 * exactly the substitution the contract is shaped to prevent. Editing means
 * asking Hermes for a different draft.
 */

import type { ChatMessageDto, ChatProposedActionDto, ChatSessionDto } from '@crm/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCrmClient } from './client.ts';
import { queryKeys } from './keys.ts';
import { useOrgId } from './session.ts';

export type { ChatMessageDto, ChatProposedActionDto, ChatSessionDto };

export function useChatSessionsQuery(includeArchived = false) {
  const client = useCrmClient();
  const orgId = useOrgId();

  return useQuery({
    queryKey: [...queryKeys.chatSessions(orgId), includeArchived] as const,
    queryFn: () => client.hermes.chat.sessions({ limit: 30, includeArchived }),
  });
}

/**
 * One transcript.
 *
 * `enabled` is false for the empty session id, which is the state of a brand-new
 * conversation that has not been sent yet — there is no server row to read, and
 * firing this would 404 on a session the user is still typing into.
 */
export function useChatHistoryQuery(sessionId: string | null) {
  const client = useCrmClient();
  const orgId = useOrgId();

  return useQuery({
    queryKey: queryKeys.chatHistory(orgId, sessionId ?? ''),
    queryFn: () => client.hermes.chat.history({ sessionId: sessionId ?? '', limit: 60 }),
    enabled: sessionId !== null && sessionId !== '',
  });
}

export type ChatSendVariables = {
  /** Absent starts a new conversation; the response names the id. */
  readonly sessionId?: string | undefined;
  readonly message: string;
};

export type ChatSendResult = {
  readonly sessionId: string;
  readonly reply: string | null;
  readonly droppedReason: string | null;
  readonly message: string | null;
  readonly messages: readonly ChatMessageDto[];
  readonly proposedActions: readonly ChatProposedActionDto[];
  readonly toolCallsUsed: number;
  readonly toolLimitReached: boolean;
  readonly modelCalls: number;
};

/**
 * One turn.
 *
 * The session list is invalidated on success even for an existing session:
 * `messageCount`, `toolCallsUsed` and `lastMessageAt` all moved, and those are
 * what the list renders. The history is invalidated rather than appended-to
 * because the server is the ordering authority — `seq` is assigned there, and a
 * client that spliced its own copy would eventually disagree about the order of
 * a turn's tool rows.
 */
export function useChatSendMutation() {
  const client = useCrmClient();
  const queryClient = useQueryClient();
  const orgId = useOrgId();

  return useMutation({
    mutationFn: ({ sessionId, message }: ChatSendVariables) =>
      client.hermes.chat.send({
        message,
        ...(sessionId === undefined || sessionId === '' ? {} : { sessionId }),
      }),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.chatSessions(orgId) });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.chatHistory(orgId, result.sessionId),
      });
    },
  });
}

export type ChatActionDecision = {
  readonly actionId: string;
  readonly sessionId: string;
  readonly decision: 'confirm' | 'cancel';
};

/**
 * Approve or decline a proposed write.
 *
 * A confirm can create a follow-up or a draft, so the digest and follow-up
 * caches are invalidated too — otherwise the item Hermes just scheduled would be
 * missing from `/today` until the next refetch, and the user would reasonably
 * conclude the approval did nothing.
 */
export function useChatActionMutation() {
  const client = useCrmClient();
  const queryClient = useQueryClient();
  const orgId = useOrgId();

  return useMutation({
    mutationFn: ({ actionId, decision }: ChatActionDecision) =>
      decision === 'confirm'
        ? client.hermes.chat.confirm({ actionId })
        : client.hermes.chat.cancel({ actionId }),
    onSuccess: (_result, variables) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.chatHistory(orgId, variables.sessionId),
      });
      if (variables.decision === 'confirm') {
        void queryClient.invalidateQueries({ queryKey: queryKeys.followUps(orgId) });
        void queryClient.invalidateQueries({ queryKey: queryKeys.digest(orgId) });
        void queryClient.invalidateQueries({ queryKey: queryKeys.drafts(orgId) });
      }
    },
  });
}
